import { create } from 'zustand';
import type { CompileResult, DeployResult } from '@/types';
import * as deployService from '@/services/deploy.service';
import type { TestResult, ToolchainInfo, AnvilResult } from '@/services/deploy.service';
import {
  notifyCompileSuccess,
  notifyCompileError,
  notifyDeploySuccess,
  notifyDeployError,
  notifyTestSuccess,
  notifyTestError,
} from './notificationStore';
import { useContractStore } from './contractStore';

interface DeployState {
  compileResult: CompileResult | null;
  deployResult: DeployResult | null;
  deployHistory: DeployResult[];
  isCompiling: boolean;
  isDeploying: boolean;
  isTesting: boolean;
  testResult: TestResult | null;
  toolchainInfo: ToolchainInfo | null;
  anvilResult: AnvilResult | null;

  compile: (workspaceId: string, toolchain?: 'hardhat' | 'foundry') => Promise<void>;
  deploy: (workspaceId: string, options?: deployService.DeployOptions) => Promise<void>;
  runTests: (workspaceId: string, options?: deployService.TestOptions) => Promise<void>;
  fetchToolchainInfo: (workspaceId: string) => Promise<void>;
  startAnvil: (workspaceId: string, options?: deployService.AnvilOptions) => Promise<void>;
  clearResults: () => void;
}

export const useDeployStore = create<DeployState>((set, get) => ({
  compileResult: null,
  deployResult: null,
  deployHistory: [],
  isCompiling: false,
  isDeploying: false,
  isTesting: false,
  testResult: null,
  toolchainInfo: null,
  anvilResult: null,

  compile: async (workspaceId, toolchain) => {
    set({ isCompiling: true, compileResult: null });
    try {
      const result = await deployService.compile(workspaceId, toolchain);
      set({ compileResult: result });
      // Fire notification for compile result
      if (result.success) {
        notifyCompileSuccess(result.contractName);
      } else {
        const firstErr = result.errors?.[0]?.message;
        notifyCompileError(firstErr);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Compilation failed';
      set({
        compileResult: {
          success: false,
          errors: [{ severity: 'error', message: msg }],
          warnings: [],
        },
      });
      notifyCompileError(msg);
    } finally {
      set({ isCompiling: false });
    }
  },

  deploy: async (workspaceId, options = {}) => {
    set({ isDeploying: true, deployResult: null });
    try {
      const result = await deployService.deploy(workspaceId, options);
      set((s) => ({
        deployResult: result,
        deployHistory: [result, ...s.deployHistory],
      }));
      // Fire notification for deploy result
      if (result.success) {
        const network = options.network ?? 'local';
        notifyDeploySuccess(network, result.contractAddress);

        // Register in contract store → seamless Deploy→Operate transition
        const { compileResult } = get();
        if (result.contractAddress) {
          useContractStore.getState().addContract({
            contractName: compileResult?.contractName ?? options.contract ?? 'Contract',
            address: result.contractAddress,
            network,
            chainId: 0, // Will be filled by wallet context
            abi: compileResult?.abi ?? [],
            bytecode: compileResult?.bytecode,
            deployedAt: Date.now(),
            transactionHash: result.transactionHash,
            blockNumber: result.blockNumber,
            gasUsed: result.gasUsed,
            workspaceId,
          });
        }
      } else {
        notifyDeployError(result.error);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Deployment failed';
      set({
        deployResult: { success: false, error: msg },
      });
      notifyDeployError(msg);
    } finally {
      set({ isDeploying: false });
    }
  },

  runTests: async (workspaceId, options = {}) => {
    set({ isTesting: true, testResult: null });
    try {
      const result = await deployService.runTests(workspaceId, options);
      set({ testResult: result });
      if (result.success) {
        notifyTestSuccess(result.passed, result.failed, result.skipped);
      } else {
        notifyTestError(undefined, result.passed, result.failed, result.skipped);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Test run failed';
      notifyTestError(msg);
      throw err;
    } finally {
      set({ isTesting: false });
    }
  },

  fetchToolchainInfo: async (workspaceId) => {
    try {
      const info = await deployService.getToolchainInfo(workspaceId);
      set({ toolchainInfo: info });
    } catch { /* ignore */ }
  },

  startAnvil: async (workspaceId, options = {}) => {
    try {
      const result = await deployService.startAnvil(workspaceId, options);
      set({ anvilResult: result });
    } catch { /* ignore */ }
  },

  clearResults: () => {
    set({ compileResult: null, deployResult: null, testResult: null });
  },
}));
